import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import {
  addRegistrationItem,
  createDealRegistration,
  createDocumentTemplate,
  deleteDocument,
  deleteDocumentTemplate,
  deleteRegistration,
  deleteRegistrationItem,
  fetchDealDocuments,
  fetchDealRegistration,
  fetchDocumentTemplates,
  fetchRegistrations,
  generateDocument,
  patchDocument,
  patchRegistration,
  patchRegistrationItem,
  updateDocumentTemplate,
  uploadDocumentFile,
  type DocumentPatchBody,
  type DocumentTemplateWriteBody,
  type RegistrationItemAddBody,
  type RegistrationItemPatchBody,
  type RegistrationPatchBody,
} from './api'

// --- Query key factory (cache contract: list = ["<entity>"], detail = ["<entity>", id]) ---

export const DOCUMENTS_KEYS = {
  templates: ['document-templates'] as const,
  dealDocuments: (dealId: string) => ['documents', 'deal', dealId] as const,
  registrations: ['registrations'] as const,
  registration: (id: string) => ['registrations', id] as const,
  dealRegistration: (dealId: string) => ['registrations', 'deal', dealId] as const,
}

// --- Outbox keys (see apps/frontend/AGENTS.md "offline outbox") ---

export const DOC_TEMPLATE_CREATE_KEY = ['outbox', 'document-templates', 'create'] as const
export const DOC_TEMPLATE_UPDATE_KEY = ['outbox', 'document-templates', 'update'] as const
export const DOC_TEMPLATE_DELETE_KEY = ['outbox', 'document-templates', 'delete'] as const
export const DOC_GENERATE_KEY = ['outbox', 'documents', 'generate'] as const
export const DOC_PATCH_KEY = ['outbox', 'documents', 'patch'] as const
export const DOC_DELETE_KEY = ['outbox', 'documents', 'delete'] as const
export const REGISTRATION_CREATE_KEY = ['outbox', 'registrations', 'create'] as const
export const REGISTRATION_PATCH_KEY = ['outbox', 'registrations', 'patch'] as const
export const REGISTRATION_DELETE_KEY = ['outbox', 'registrations', 'delete'] as const
export const REGISTRATION_ITEM_ADD_KEY = ['outbox', 'registration-items', 'add'] as const
export const REGISTRATION_ITEM_PATCH_KEY = ['outbox', 'registration-items', 'patch'] as const
export const REGISTRATION_ITEM_DELETE_KEY = ['outbox', 'registration-items', 'delete'] as const

// Replay functions must mirror the hook mutationFn exactly (same imported
// function) so an offline replay behaves identically to an online submit.
// Deal/registration-scoped hooks carry their scope id in the variables
// (injected by the hook, not a closure) so a persisted replay can rebuild the
// request path after a reload.
queryClient.setMutationDefaults(DOC_TEMPLATE_CREATE_KEY, { mutationFn: createDocumentTemplate })
queryClient.setMutationDefaults(DOC_TEMPLATE_UPDATE_KEY, {
  mutationFn: ({ id, body }: { id: string; body: DocumentTemplateWriteBody }) =>
    updateDocumentTemplate(id, body),
})
queryClient.setMutationDefaults(DOC_TEMPLATE_DELETE_KEY, { mutationFn: deleteDocumentTemplate })
queryClient.setMutationDefaults(DOC_GENERATE_KEY, {
  mutationFn: ({ dealId, templateId }: { dealId: string; templateId: string }) =>
    generateDocument(dealId, templateId),
})
queryClient.setMutationDefaults(DOC_PATCH_KEY, {
  mutationFn: ({ id, body }: { id: string; body: DocumentPatchBody }) =>
    patchDocument(id, body),
})
queryClient.setMutationDefaults(DOC_DELETE_KEY, { mutationFn: deleteDocument })
queryClient.setMutationDefaults(REGISTRATION_CREATE_KEY, {
  mutationFn: ({ dealId }: { dealId: string }) => createDealRegistration(dealId),
})
queryClient.setMutationDefaults(REGISTRATION_PATCH_KEY, {
  mutationFn: ({ id, body }: { id: string; body: RegistrationPatchBody }) =>
    patchRegistration(id, body),
})
queryClient.setMutationDefaults(REGISTRATION_DELETE_KEY, { mutationFn: deleteRegistration })
queryClient.setMutationDefaults(REGISTRATION_ITEM_ADD_KEY, {
  mutationFn: ({ registrationId, body }: { registrationId: string; body: RegistrationItemAddBody }) =>
    addRegistrationItem(registrationId, body),
})
queryClient.setMutationDefaults(REGISTRATION_ITEM_PATCH_KEY, {
  mutationFn: ({
    registrationId,
    itemId,
    body,
  }: {
    registrationId: string
    itemId: string
    body: RegistrationItemPatchBody
  }) => patchRegistrationItem(registrationId, itemId, body),
})
queryClient.setMutationDefaults(REGISTRATION_ITEM_DELETE_KEY, {
  mutationFn: ({ registrationId, itemId }: { registrationId: string; itemId: string }) =>
    deleteRegistrationItem(registrationId, itemId),
})

// --- Templates ---

export const useDocumentTemplates = () =>
  useQuery({
    queryKey: DOCUMENTS_KEYS.templates,
    queryFn: fetchDocumentTemplates,
  })

export const useCreateDocumentTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: DOC_TEMPLATE_CREATE_KEY,
    mutationFn: createDocumentTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.templates }),
  })
}

export const useUpdateDocumentTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: DOC_TEMPLATE_UPDATE_KEY,
    mutationFn: ({ id, body }: { id: string; body: DocumentTemplateWriteBody }) =>
      updateDocumentTemplate(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.templates }),
  })
}

export const useDeleteDocumentTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: DOC_TEMPLATE_DELETE_KEY,
    mutationFn: deleteDocumentTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.templates }),
  })
}

// --- Deal documents ---

export const useDealDocuments = (dealId: string) =>
  useQuery({
    queryKey: DOCUMENTS_KEYS.dealDocuments(dealId),
    queryFn: () => fetchDealDocuments(dealId),
  })

// dealId rides in the variables (injected here) so an offline replay can
// rebuild the request path after a reload.
export const useGenerateDocument = (dealId: string) => {
  const qc = useQueryClient()
  const mutation = useMutation<
    Awaited<ReturnType<typeof generateDocument>>,
    Error,
    { dealId: string; templateId: string }
  >({
    mutationKey: DOC_GENERATE_KEY,
    mutationFn: ({ dealId: dId, templateId }) => generateDocument(dId, templateId),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.dealDocuments(dealId) }),
  })
  return {
    ...mutation,
    mutate: (templateId: string, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ dealId, templateId }, options),
    mutateAsync: (templateId: string, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync({ dealId, templateId }, options),
  }
}

export const usePatchDocument = (dealId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: DOC_PATCH_KEY,
    mutationFn: ({ id, body }: { id: string; body: DocumentPatchBody }) =>
      patchDocument(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.dealDocuments(dealId) }),
  })
}

export const useUploadDocumentFile = (dealId: string) => {
  const qc = useQueryClient()
  return useMutation({
    // File payload can't serialize into the persisted outbox — fail fast
    // offline instead of pausing.
    networkMode: 'always',
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadDocumentFile(id, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.dealDocuments(dealId) }),
  })
}

export const useDeleteDocument = (dealId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: DOC_DELETE_KEY,
    mutationFn: deleteDocument,
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.dealDocuments(dealId) }),
  })
}

// --- Registrations ---

export const useRegistrations = () =>
  useQuery({
    queryKey: DOCUMENTS_KEYS.registrations,
    queryFn: fetchRegistrations,
  })

export const useDealRegistration = (dealId: string) =>
  useQuery({
    queryKey: DOCUMENTS_KEYS.dealRegistration(dealId),
    queryFn: () => fetchDealRegistration(dealId),
    // 404 just means the workflow has not started yet — that state drives
    // the "Start registration" button, not an error screen.
    retry: false,
  })

export const useCreateDealRegistration = (dealId: string) => {
  const qc = useQueryClient()
  const mutation = useMutation<
    Awaited<ReturnType<typeof createDealRegistration>>,
    Error,
    { dealId: string }
  >({
    mutationKey: REGISTRATION_CREATE_KEY,
    mutationFn: ({ dealId: dId }) => createDealRegistration(dId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.dealRegistration(dealId) })
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.registrations })
    },
  })
  return {
    ...mutation,
    mutate: (options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ dealId }, options),
    mutateAsync: (options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync({ dealId }, options),
  }
}

export const usePatchRegistration = (opts: { dealId?: string } = {}) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: REGISTRATION_PATCH_KEY,
    mutationFn: ({ id, body }: { id: string; body: RegistrationPatchBody }) =>
      patchRegistration(id, body),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.registrations })
      if (opts.dealId) {
        qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.dealRegistration(opts.dealId) })
      }
    },
  })
}

export const useDeleteRegistration = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: REGISTRATION_DELETE_KEY,
    mutationFn: deleteRegistration,
    onSuccess: () => qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.registrations }),
  })
}

// registrationId rides in the variables (injected here) so an offline replay
// can rebuild the request path after a reload.
export const useAddRegistrationItem = (registrationId: string, dealId?: string) => {
  const qc = useQueryClient()
  const mutation = useMutation<
    Awaited<ReturnType<typeof addRegistrationItem>>,
    Error,
    { registrationId: string; body: RegistrationItemAddBody }
  >({
    mutationKey: REGISTRATION_ITEM_ADD_KEY,
    mutationFn: ({ registrationId: rId, body }) => addRegistrationItem(rId, body),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.registration(registrationId) })
      if (dealId) {
        qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.dealRegistration(dealId) })
      }
    },
  })
  return {
    ...mutation,
    mutate: (body: RegistrationItemAddBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ registrationId, body }, options),
    mutateAsync: (
      body: RegistrationItemAddBody,
      options?: Parameters<typeof mutation.mutateAsync>[1],
    ) => mutation.mutateAsync({ registrationId, body }, options),
  }
}

export const usePatchRegistrationItem = (registrationId: string, dealId?: string) => {
  const qc = useQueryClient()
  const mutation = useMutation<
    Awaited<ReturnType<typeof patchRegistrationItem>>,
    Error,
    { registrationId: string; itemId: string; body: RegistrationItemPatchBody }
  >({
    mutationKey: REGISTRATION_ITEM_PATCH_KEY,
    mutationFn: ({ registrationId: rId, itemId, body }) =>
      patchRegistrationItem(rId, itemId, body),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.registration(registrationId) })
      if (dealId) {
        qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.dealRegistration(dealId) })
      }
    },
  })
  return {
    ...mutation,
    mutate: (
      vars: { itemId: string; body: RegistrationItemPatchBody },
      options?: Parameters<typeof mutation.mutate>[1],
    ) => mutation.mutate({ registrationId, ...vars }, options),
    mutateAsync: (
      vars: { itemId: string; body: RegistrationItemPatchBody },
      options?: Parameters<typeof mutation.mutateAsync>[1],
    ) => mutation.mutateAsync({ registrationId, ...vars }, options),
  }
}

export const useDeleteRegistrationItem = (registrationId: string, dealId?: string) => {
  const qc = useQueryClient()
  const mutation = useMutation<
    Awaited<ReturnType<typeof deleteRegistrationItem>>,
    Error,
    { registrationId: string; itemId: string }
  >({
    mutationKey: REGISTRATION_ITEM_DELETE_KEY,
    mutationFn: ({ registrationId: rId, itemId }) => deleteRegistrationItem(rId, itemId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.registration(registrationId) })
      if (dealId) {
        qc.invalidateQueries({ queryKey: DOCUMENTS_KEYS.dealRegistration(dealId) })
      }
    },
  })
  return {
    ...mutation,
    mutate: (itemId: string, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ registrationId, itemId }, options),
    mutateAsync: (itemId: string, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync({ registrationId, itemId }, options),
  }
}
